import { useState } from "react";
import { Eraser, RotateCw, Trash2, Mountain, AlertTriangle, Gem, CircleDot, Truck, Fuel, Globe2, Drill, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NavGrid } from "./NavGrid";
import { DIRS, GRID, WORLDS, WORLD_IDS, DEFAULT_FUEL, MAX_FUEL, key, emptyNav, worldMeta, navGoals, POWER_LABEL, POWER_TIP, GEM_TIP, MAX_HIDDEN_GEMS } from "./navShared";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const same = (a, b) => a && b && a.x === b.x && a.y === b.y;
const without = (list, cell) => (list || []).filter((c) => !same(c, cell));

// Remove whatever sits on a cell (rocks, dangers, tunnel pairs, fuel cells); start/goals are moved by their own tools
const clearCell = (nav, cell) => ({
  ...nav,
  rocks: without(nav.rocks, cell),
  dangers: without(nav.dangers, cell),
  fuel_cells: without(nav.fuel_cells, cell),
  powers: without(nav.powers, cell),
  gems: without(nav.gems, cell),
  tunnels: (nav.tunnels || []).filter((t) => !same(t.a, cell) && !same(t.b, cell)),
});

const withGoals = (nav, goals) => ({ ...nav, goals, treasure: goals[0] || null });

// Client-side sanity (the server validates again on save)
export const navProblems = (nav) => {
  const out = [];
  const w = worldMeta(nav);
  const goals = navGoals(nav);
  const [lo, hi] = w.goalCount;
  if (!nav?.start) out.push(`Place the ${w.labels.vehicle}`);
  if (goals.length < lo) out.push(lo === hi && lo > 1 ? `${w.name} grids need exactly ${lo} ${w.labels.goals} - place ${lo - goals.length} more` : `Place the ${w.labels.goal}`);
  if (goals.length > hi) out.push(`${w.name} grids allow at most ${hi} ${w.labels.goals}`);
  if (goals.some((g) => same(nav?.start, g))) out.push(`The ${w.labels.vehicle} can't start on a ${w.labels.goal}`);
  if (w.fuel && (!Number.isInteger(nav?.fuel) || nav.fuel < 1 || nav.fuel > MAX_FUEL)) out.push(`Starting fuel must be between 1 and ${MAX_FUEL}`);
  (nav?.tunnels || []).forEach((t) => { if (!t.b) out.push(`Tunnel ${t.letter} needs its second end`); });
  return out;
};

// First free cell for an extra goal when switching to a multi-goal world
const freeCell = (nav) => {
  const used = new Set([key(nav.start.x, nav.start.y), ...navGoals(nav).map((g) => key(g.x, g.y)), ...(nav.rocks || []).map((c) => key(c.x, c.y)),
    ...(nav.dangers || []).map((c) => key(c.x, c.y)), ...(nav.fuel_cells || []).map((c) => key(c.x, c.y)), ...(nav.powers || []).map((c) => key(c.x, c.y)), ...(nav.gems || []).map((c) => key(c.x, c.y)),
    ...(nav.tunnels || []).flatMap((t) => [t.a, t.b].filter(Boolean).map((c) => key(c.x, c.y)))]);
  for (const c of [{ x: GRID - 1, y: GRID - 1 }, { x: 0, y: 0 }, { x: GRID - 1, y: Math.floor(GRID / 2) }, { x: Math.floor(GRID / 2), y: 0 }]) if (!used.has(key(c.x, c.y))) return c;
  for (let y = 0; y < GRID; y++) for (let x = 0; x < GRID; x++) if (!used.has(key(x, y))) return { x, y };
  return null;
};

// Switch world: keep the layout, fit the goal count, add/remove fuel
export const changeWorld = (nav, world) => {
  const w = WORLDS[world] || WORLDS.mines;
  let goals = navGoals(nav).slice(0, w.goalCount[1]);
  let next = { ...nav, world: w.id };
  while (goals.length < w.goalCount[0]) {
    const c = freeCell(withGoals(next, goals));
    if (!c) break;
    goals = [...goals, c];
  }
  next = withGoals(next, goals);
  return w.fuel ? { ...next, fuel: Number.isInteger(next.fuel) ? next.fuel : DEFAULT_FUEL, fuel_cells: next.fuel_cells || [] } : { ...next, fuel: null, fuel_cells: [] };
};

// Click-to-place grid editor for navigation questions. `allowWorlds` shows the World picker (Bonus challenges only).
export const NavGridEditor = ({ nav: navProp, onChange, allowWorlds = false }) => {
  const nav = navProp || emptyNav();
  const w = worldMeta(nav);
  const [tool, setTool] = useState("rock");
  const pending = (nav.tunnels || []).find((t) => !t.b);
  const goals = navGoals(nav);
  const maxGoals = w.goalCount[1];

  const tools = [
    { id: "drill", label: cap(w.labels.vehicle), icon: Truck },
    { id: "treasure", label: cap(w.labels.goal), icon: Gem },
    { id: "rock", label: cap(w.labels.obstacle), icon: Mountain },
    { id: "danger", label: cap(w.labels.danger), icon: AlertTriangle },
    ...(w.fuel ? [{ id: "fuel", label: "Fuel cell", icon: Fuel }] : []),
    { id: "power", label: POWER_LABEL, icon: Drill, tip: POWER_TIP },
    { id: "gem", label: "Hidden gem", icon: Eye, tip: GEM_TIP },
    { id: "tunnel", label: "Tunnel pair", icon: CircleDot },
    { id: "erase", label: "Eraser", icon: Eraser },
  ];

  const update = (next) => onChange({ ...nav, ...next });
  const rotate = () => update({ start: { ...nav.start, dir: DIRS[(DIRS.indexOf(nav.start.dir) + 1) % 4] } });

  const onCell = (x, y) => {
    const cell = { x, y };
    if (tool === "erase") return onChange(clearCell(nav, cell));
    if (same(nav.start, cell) && tool !== "drill") return; // vehicle stays unless moved with its tool
    const onGoal = goals.some((g) => same(g, cell));
    if (onGoal && tool !== "treasure") return;
    const base = clearCell(nav, cell);
    if (tool === "drill") return onChange({ ...base, start: { ...cell, dir: nav.start?.dir || "N" } });
    if (tool === "treasure") {
      if (onGoal) return maxGoals > 1 ? onChange(withGoals(base, goals.filter((g) => !same(g, cell)))) : undefined;
      if (maxGoals === 1) return onChange(withGoals(base, [cell]));
      if (goals.length >= maxGoals) return;
      return onChange(withGoals(base, [...goals, cell]));
    }
    if (tool === "rock") return onChange({ ...base, rocks: nav.rocks?.some((c) => same(c, cell)) ? without(nav.rocks, cell) : [...(base.rocks || []), cell] });
    if (tool === "danger") return onChange({ ...base, dangers: nav.dangers?.some((c) => same(c, cell)) ? without(nav.dangers, cell) : [...(base.dangers || []), cell] });
    if (tool === "fuel") return onChange({ ...base, fuel_cells: nav.fuel_cells?.some((c) => same(c, cell)) ? without(nav.fuel_cells, cell) : [...(base.fuel_cells || []), cell] });
    if (tool === "power") return onChange({ ...base, powers: nav.powers?.some((c) => same(c, cell)) ? without(nav.powers, cell) : [...(base.powers || []), cell] });
    if (tool === "gem") {
      if (nav.gems?.some((c) => same(c, cell))) return onChange({ ...base, gems: without(nav.gems, cell) });
      if ((nav.gems || []).length >= MAX_HIDDEN_GEMS) return;
      return onChange({ ...base, gems: [...(base.gems || []), cell] });
    }
    if (tool === "tunnel") {
      if (pending) {
        return onChange({ ...base, tunnels: base.tunnels.map((t) => (t.letter === pending.letter ? { ...t, b: cell } : t)) });
      }
      const used = new Set((nav.tunnels || []).map((t) => t.letter));
      const letter = LETTERS.split("").find((l) => !used.has(l));
      if (!letter) return;
      return onChange({ ...base, tunnels: [...base.tunnels, { letter, a: cell, b: null }] });
    }
  };

  const problems = navProblems(nav);
  const help = () => {
    if (tool === "tunnel") return pending ? `Click a second cell to finish tunnel ${pending.letter}.` : "Click two cells to create a tunnel pair.";
    if (tool === "erase") return "Click an item to remove it.";
    if (tool === "drill") return `Click a cell to place the ${w.labels.vehicle}.`;
    if (tool === "treasure") return maxGoals > 1 ? `Click cells to place up to ${maxGoals} ${w.labels.goals} (click one again to remove it). ${goals.length} of ${maxGoals} placed.` : `Click a cell to place the ${w.labels.goal}.`;
    if (tool === "fuel") return "Click a cell to place a fuel cell (+10 fuel when the drill moves onto it). Click again to remove.";
    if (tool === "gem") return `Click a cell to hide a gem (up to ${MAX_HIDDEN_GEMS} per grid${(nav.gems || []).length >= MAX_HIDDEN_GEMS ? " - limit reached, click a gem to remove it" : ""}). Players never see it: a loop or if turns the scanner on and reveals any gem in the 3x3 around the ${w.labels.vehicle} for +10 each. Click again to remove.`;
    if (tool === "power") return `Click a cell to place an activate-drill power-up. Picking one up turns the drill on: the next forward move bores through one ${w.labels.obstacle}, then it switches off (each power-up = one ${w.labels.obstacle}; reversing still bumps). Click again to remove.`;
    return `Click a cell to place a ${tool === "rock" ? w.labels.obstacle : w.labels.danger} (click again to remove).`;
  };

  return (
    <div className="space-y-3" data-testid="nav-grid-editor" data-world={w.id}>
      {(allowWorlds || w.id !== "mines") && (
        <div className="flex flex-wrap items-center gap-2" data-testid="nav-world-picker">
          <span className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1"><Globe2 className="w-3.5 h-3.5" /> World</span>
          {WORLD_IDS.map((id) => (
            <Button key={id} type="button" size="sm" variant={w.id === id ? "default" : "outline"} onClick={() => onChange(changeWorld(nav, id))} className="gap-1.5 rounded-full" disabled={!allowWorlds && id !== "mines"} data-testid={`nav-world-${id}`}>
              <img src={WORLDS[id].sprites.vehicle} alt="" className="w-4 h-4" /> {WORLDS[id].name}
            </Button>
          ))}
          {!allowWorlds && w.id !== "mines" && (
            <span className="text-xs text-amber-600 dark:text-amber-400" data-testid="nav-world-pool-warning">Ocean and Moon are only for Bonus challenges - switch the pool to Challenge or go back to Mines.</span>
          )}
          {w.fuel && (
            <label className="inline-flex items-center gap-2 text-xs font-medium ml-auto" data-testid="nav-fuel-field">
              <Fuel className="w-3.5 h-3.5 text-muted-foreground" /> Starting fuel
              <Input type="number" min={1} max={MAX_FUEL} className="h-8 w-20" value={nav.fuel ?? DEFAULT_FUEL}
                onChange={(e) => update({ fuel: e.target.value === "" ? "" : Math.min(MAX_FUEL, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                data-testid="nav-fuel-input" />
              <span className="text-muted-foreground font-normal">1–{MAX_FUEL}, −1 per move</span>
            </label>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 items-center">
        {tools.map(({ id, label, icon: Icon, tip }) => (
          <Button key={id} type="button" size="sm" variant={tool === id ? "default" : "outline"} onClick={() => setTool(id)} className="gap-1.5 rounded-full" title={tip} data-testid={`nav-tool-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </Button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <Button type="button" size="sm" variant="outline" onClick={rotate} className="gap-1.5 rounded-full" data-testid="nav-rotate-drill-btn" title={`Rotate the ${w.labels.vehicle}`}>
          <RotateCw className="w-3.5 h-3.5" /> Facing {nav.start?.dir || "N"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...nav, rocks: [], dangers: [], tunnels: [], fuel_cells: [], powers: [], gems: [] })} className="gap-1.5 rounded-full text-muted-foreground" data-testid="nav-clear-btn">
          <Trash2 className="w-3.5 h-3.5" /> Clear items
        </Button>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="nav-tool-help">{help()}</p>
      <div className="max-w-md">
        <NavGrid nav={nav} onCellClick={onCell} cellTestId="nav-edit-cell" hiddenGems={nav.gems} />
      </div>
      {problems.length > 0 && (
        <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc pl-4" data-testid="nav-grid-problems">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      <p className="text-xs text-muted-foreground" data-testid="nav-grid-summary">
        {w.name} · {(nav.rocks || []).length} {w.labels.obstacles} · {(nav.dangers || []).length} {w.labels.dangers} · {(nav.tunnels || []).filter((t) => t.b).length} tunnel pairs
        {w.fuel ? ` · ${(nav.fuel_cells || []).length} fuel cells · ${nav.fuel ?? DEFAULT_FUEL} starting fuel` : ""}{(nav.powers || []).length ? ` · ${nav.powers.length} activate-drill power-up${nav.powers.length === 1 ? "" : "s"}` : ""}{(nav.gems || []).length ? ` · ${nav.gems.length} hidden gem${nav.gems.length === 1 ? "" : "s"}` : ""} · {w.labels.vehicle} at ({nav.start?.x}, {nav.start?.y}) facing {nav.start?.dir}
        {" · "}{goals.length === 1 ? `${w.labels.goal} at (${goals[0].x}, ${goals[0].y})` : `${goals.length} ${w.labels.goals}: ${goals.map((g) => `(${g.x}, ${g.y})`).join(" ")}`}
      </p>
    </div>
  );
};

export { key };
